import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SITE_VIDEOS } from '../../data/videos';
import { SOCIAL_LINKS } from '../../data/social';
import { SHOP_ENABLED } from '../../data/shop';

const menuItems = [
  { num: '01', name: 'HOME', target: 'home' },
  { num: '02', name: 'WORK', target: 'work-page' },
  { num: '03', name: 'ABOUT', target: 'about-page' },
  ...(SHOP_ENABLED ? [{ num: '04', name: 'SHOP', target: 'shop-page' }] : []),
  { num: SHOP_ENABLED ? '05' : '04', name: 'CONTACT', target: 'contact' },
];

const NAV_TARGETS = new Set(['home', 'work-page', 'about-page', 'contact']);

const MENU_FOOTER_LABEL = 'flex items-center gap-1 mb-0';
const MENU_FOOTER_PLUS = 'text-[#555] text-[10px] leading-none shrink-0';
const MENU_FOOTER_HEADING =
  'font-inter text-[9px] md:text-[11px] tracking-[0.15em] text-[#555] uppercase leading-none';
const MENU_FOOTER_LINKS =
  'flex flex-wrap items-baseline gap-x-3 gap-y-1 md:flex-col md:items-start md:gap-1';
const MENU_FOOTER_LINK =
  'inline-block font-inter text-[11px] md:text-[13px] leading-none text-[#666] hover:text-white transition-colors p-0 m-0 border-0 bg-transparent cursor-pointer font-normal text-left';

const FullScreenMenu = ({ isOpen, onClose, onNavigate, onOpenContact }) => {
  const pendingNavRef = useRef(null);
  const menuVideoRef = useRef(null);

  // Block body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';

      const pending = pendingNavRef.current;
      if (!pending || !NAV_TARGETS.has(pending)) {
        window.scrollTo(0, parseInt(scrollY || '0', 10) * -1);
      }
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const video = menuVideoRef.current;
    if (!video) return undefined;

    if (isOpen) {
      video.currentTime = 0;
      const playPromise = video.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    } else {
      video.pause();
    }

    return undefined;
  }, [isOpen]);

  const handleClick = (target) => {
    pendingNavRef.current = target;
    onClose();
    setTimeout(() => {
      onNavigate(target);
      pendingNavRef.current = null;
    }, 400);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[90] text-white flex flex-col md:flex-row"
          style={{ backgroundColor: 'var(--color-ink)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Video section - 30% on mobile, 45% on desktop */}
          <div className="w-full md:w-[45%] aspect-[4/5] md:aspect-auto md:h-full max-h-[42vh] md:max-h-none relative overflow-hidden flex-shrink-0 bg-[var(--color-charcoal)]">
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              initial={{ scale: 1.1, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <video
                ref={menuVideoRef}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                className="w-full h-full object-cover object-center"
                aria-label="Menu reel"
              >
                <source src={SITE_VIDEOS.menu} type="video/mp4" />
              </video>
              <div className="absolute inset-0" style={{ backgroundColor: 'var(--overlay-dim)' }} />
            </motion.div>
          </div>

          {/* Navigation section - fills remaining 70% on mobile */}
          <div className="w-full md:w-[55%] flex-1 flex flex-col px-4 md:px-16 py-4 md:py-8">
            {/* Navigation Links */}
            <nav className="flex flex-col flex-1 justify-center">
              {menuItems.map((item, i) => (
                <motion.div
                  key={item.name}
                  className="border-b border-[#222] py-[10px] md:py-5 group"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + i * 0.06, duration: 0.4 }}
                >
                  <button
                    className="w-full flex items-center justify-between"
                    onClick={() => handleClick(item.target)}
                    data-testid={`menu-item-${item.name.toLowerCase()}`}
                  >
                    <div className="flex items-center gap-4 md:gap-8">
                      <span className="font-inter text-[11px] md:text-[14px] text-[#555] tracking-wider">{item.num}</span>
                      <span className="font-inter text-[20px] sm:text-[24px] md:text-[42px] font-medium text-white tracking-wide uppercase">
                        {item.name}
                      </span>
                    </div>
                    <span
                      className="font-inter text-[14px] md:text-[20px] text-[#555] transition-colors duration-300 group-hover:text-white"
                      aria-hidden="true"
                    >
                      +
                    </span>
                  </button>
                </motion.div>
              ))}
            </nav>

            {/* Bottom section - Follow & Contact */}
            <motion.div
              className="grid grid-cols-2 gap-x-8 md:gap-x-24 gap-y-1 md:gap-y-3 pt-4 md:pt-6 border-t border-[#222] items-start"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <div className={MENU_FOOTER_LABEL}>
                <span className={MENU_FOOTER_PLUS}>+</span>
                <span className={MENU_FOOTER_HEADING}>FOLLOW</span>
              </div>
              <div className={MENU_FOOTER_LABEL}>
                <span className={MENU_FOOTER_PLUS}>+</span>
                <span className={MENU_FOOTER_HEADING}>CONTACT</span>
              </div>
              <div className={MENU_FOOTER_LINKS}>
                {SOCIAL_LINKS.map((social) => (
                  <a
                    key={social.id}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={MENU_FOOTER_LINK}
                  >
                    {social.label}
                  </a>
                ))}
              </div>
              <div className={MENU_FOOTER_LINKS}>
                <button
                  type="button"
                  className={MENU_FOOTER_LINK}
                  onClick={() => {
                    onClose();
                    setTimeout(() => onOpenContact?.(), 400);
                  }}
                >
                  Contact
                </button>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FullScreenMenu;
